import moment from "moment";
import { DocumentType } from "src/utils/enum";
import { formatRut } from "../../../../../../utils/formatter";
import { formatBanks, banksFormat } from "../../../../../../utils/bankList";
import { Obligaciones } from "../../../../../../utils/obligations";
import { dt, translateDocumentSummary, translateObligationType } from "../../../../../../utils/documentsI18n";

/**
 * Catálogo canónico de obligaciones provisto por el backend (ObligationsContext / getObligations).
 * Es la misma fuente que escribe `meta.type` del documento (type = generateKey(typeLabel)), por lo
 * que el match es exacto incluso para tipos con paréntesis como "pagoprovisionalmensual(ppm)".
 * Se llena en runtime (ver MatchList); si está vacío se cae al catálogo estático + i18n.
 */
let backendObligationCatalog = [];

const normalizeObligationType = (value) => String(value ?? "").trim().toLowerCase();

/** Tipos "Rendición por …" (excluidos del catálogo por defecto en getObligations). */
const RENDICION_POR_TYPE_LABELS = {
  rendicionporfondoporrendir: "Rendición por Fondo por Rendir",
  rendicionportraspasoentrecuentas: "Rendición por Traspaso entre Cuentas",
  rendicionporregularizacionbancaria: "Rendición por Regularización Bancaria",
  rendicionporanticipodecliente: "Rendición por Anticipo de Cliente",
  rendicionporanticipoproveedor: "Rendición por Anticipo Proveedor",
  rendicionporanticipodehonorarios: "Rendición por Anticipo de Honorarios",
};

const normalizeObligationTypeKey = (value) =>
  String(value ?? "").trim().toLowerCase().replace(/_/g, "");

const isHumanReadableTypeLabel = (typeLabel, typeKey) => {
  const trimmed = String(typeLabel ?? "").trim();
  if (!trimmed) return false;
  if (/\s/.test(trimmed)) return true;
  return normalizeObligationTypeKey(trimmed) !== normalizeObligationTypeKey(typeKey);
};

/** Sincroniza el catálogo de obligaciones del backend usado para resolver labels. */
export const setObligationCatalog = (list) => {
  backendObligationCatalog = Array.isArray(list) ? list : [];
};

/** typeLabel canónico del backend para un meta.type (match exacto por type/typeKey). */
const findBackendObligationLabel = (typeKey) => {
  if (!backendObligationCatalog.length || !typeKey) return null;
  const target = normalizeObligationType(typeKey);
  const found = backendObligationCatalog.find(
    (o) =>
      normalizeObligationType(o?.type) === target ||
      normalizeObligationType(o?.typeKey) === target
  );
  const label = found?.typeLabel != null ? String(found.typeLabel).trim() : "";
  return label || null;
};

/**
 * Obtener el label de visualización para el tipo de obligación (meta.type / meta.typeLabel).
 * Prioridad: typeLabel del documento → tipos rendición por … → catálogo backend → catálogo estático + i18n.
 */
export const getObligationTypeDisplayLabel = (meta) => {
  if (!meta) return dt("reconcile.unidentifiedObligation");
  const type = (meta.type || "").trim().toLowerCase();
  const normalizedType = normalizeObligationTypeKey(type);
  const typeKey =
    normalizedType ||
    normalizeObligationTypeKey(meta.typeLabel) ||
    "";
  if (!typeKey) return dt("reconcile.unidentifiedObligation");

  if (typeof meta.typeLabel === "string" && isHumanReadableTypeLabel(meta.typeLabel, typeKey)) {
    return meta.typeLabel.trim();
  }

  if (RENDICION_POR_TYPE_LABELS[normalizedType]) {
    return RENDICION_POR_TYPE_LABELS[normalizedType];
  }

  const backendLabel = findBackendObligationLabel(typeKey);
  if (backendLabel) return backendLabel;

  const subAreas = Obligaciones.flatMap((area) => area.subAreas || []);
  const found = subAreas.find(
    (s) =>
      (s.type && s.type.toLowerCase() === typeKey) ||
      (s.type && s.type.toLowerCase().replace(/_/g, "") === typeKey.replace(/_/g, ""))
  );
  if (found?.type) return translateObligationType(found.type);
  return translateObligationType(typeKey);
};

/**
 * Collapse pipe-separated glosas to the first segment + "(y N item(s) más)" (same wording as meta.detail).
 */
const summarizePipeSeparatedText = (text) => {
  if (text == null || typeof text !== "string") return "";
  const trimmed = text.trim();
  const parts = trimmed.split("|").map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) {
    return trimmed;
  }
  const additionalItemsCount = parts.length - 1;
  const itemWord =
    additionalItemsCount === 1 ? dt("reconcile.item") : dt("reconcile.items");
  return `${parts[0]}${dt("reconcile.moreItems", { count: additionalItemsCount, itemWord })}`;
};

/**
 * Build glosa from line items (TAX_DOCUMENT meta.description or OBLIGATION meta.detail).
 * Uses name + non-empty description only (never item index as glosa).
 */
const buildDescriptionFromLineItems = (items) => {
  if (!Array.isArray(items) || items.length === 0) return "";

  const firstItem = items[0];
  if (typeof firstItem === "string") {
    let result = firstItem.trim();
    if (items.length > 1 && result) {
      const additionalItemsCount = items.length - 1;
      const itemText = additionalItemsCount === 1 ? "item" : "items";
      result = `${result}... (y ${additionalItemsCount} ${itemText} más)`;
    }
    return result;
  }

  const name =
    typeof firstItem?.name === "string" ? firstItem.name.trim() : "";
  const desc =
    typeof firstItem?.description === "string"
      ? firstItem.description.trim()
      : "";

  let result = "";
  if (name && desc) {
    result = `${name} ${desc}`;
  } else if (name) {
    result = name;
  } else if (desc) {
    result = desc;
  }

  if (items.length > 1 && result) {
    const additionalItemsCount = items.length - 1;
    const itemText = additionalItemsCount === 1 ? "item" : "items";
    result = `${result}... (y ${additionalItemsCount} ${itemText} más)`;
  }

  return result;
};

/**
 * Extract description (glosa). For obligations glosa is in meta.description (string or array). Also meta.detail.
 * If both name and description exist, concatenate them (name first, then description)
 * If only one exists, show that one
 * If neither exists, return ''
 * If array has more than 1 element, append "... (y X items más)"
 */
export const getDescription = (match) => {
  if (match.meta?.description !== undefined && match.meta.description !== null) {
    if (typeof match.meta.description === "string") {
      return summarizePipeSeparatedText(match.meta.description);
    }
    if (Array.isArray(match.meta.description) && match.meta.description.length > 0) {
      return buildDescriptionFromLineItems(match.meta.description);
    }
  }

  if (match.meta?.descriptionText && typeof match.meta.descriptionText === "string") {
    return summarizePipeSeparatedText(match.meta.descriptionText);
  }

  const detail = match.meta?.detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return buildDescriptionFromLineItems(detail);
  }

  if (match.description && typeof match.description === "string") {
    return summarizePipeSeparatedText(match.description);
  }
  return "";
};

/**
 * Extract name based on isReceived status
 * For CREDIT documents, show the identifier in the description position
 */
export const getName = (match) => {
  // For CREDIT documents, show identifier in the description position
  if (match.documentType === DocumentType.CREDIT) {
    const bankName = banksFormat[match.name] || match.name || '';
    const identificator = match.meta?.identificator || '';
    if (bankName && identificator) {
      return `${bankName}: ${identificator}`;
    } else if (identificator) {
      return identificator;
    }
    return '';
  }

  const descriptionText = getDescription(match);

  // Helper to get unformatted RUT (without dots)
  const getUnformattedRut = (rut) => {
    if (!rut) return '';
    return String(rut).replace(/\./g, '');
  };

  const suffix = descriptionText ? ` :: ${descriptionText}` : "";
  return [DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION].includes(
    match.documentType
  )
    ? match.meta?.isReceived
      ? match.meta?.issuerName
        ? dt("reconcile.issuedByName", {
            name: match.meta.issuerName,
            rut: match.meta?.issuerId ? ` ${getUnformattedRut(match.meta.issuerId)}` : "",
            suffix,
          })
        : match.meta?.issuerId
          ? dt("reconcile.issuedByRut", { rut: formatRut(match.meta.issuerId), suffix })
          : descriptionText || "-"
      : match.meta?.receiverName
        ? dt("reconcile.issuedToName", {
            name: match.meta.receiverName,
            rut: match.meta?.receiverId ? ` ${getUnformattedRut(match.meta.receiverId)}` : "",
            suffix,
          })
        : match.meta?.receiverId
          ? dt("reconcile.issuedToRut", { rut: formatRut(match.meta.receiverId), suffix })
          : descriptionText || "-"
    : ["SPEND", "GIVEN", "RECIEVED"].includes(match.type)
      ? match.description || '-'
      : "";
};

/**
 * Extract upperLine text for the match
 */
export const getUpperLine = (match) => {
  // If it's a manual match with a transaction, show transaction info instead of just "Match realizado manualmente"
  if (match.settleId && ["SPEND", "GIVEN", "RECIEVED"].includes(match.type)) {
    const bankName = formatBanks[match.bankId] || match.bankId || '';
    const description = match.description || '';
    // Show transaction description and bank, or just bank if no description
    if (description && bankName) {
      return `${description} - ${bankName}`;
    } else if (description) {
      return description;
    } else if (bankName) {
      const typeLabel =
        match.type === "RECIEVED" || match.type === "RECEIVED"
          ? dt("reconcile.credit")
          : dt("reconcile.debit");
      return dt("reconcile.throughBank", { type: typeLabel, bank: bankName });
    } else {
      return match.origin == "clay"
        ? dt("reconcile.matchManualClay")
        : dt("reconcile.matchManual");
    }
  }
  
  // For manual matches with documents, show the match source message
  if (match.settleId && ["TAX_DOCUMENT", "OBLIGATION", "CREDIT"].includes(match.documentType)) {
    const matchSource =
      match.origin == "clay" ? dt("reconcile.matchManualClay") : dt("reconcile.matchManual");
    const documentInfo = ["TAX_DOCUMENT"].includes(match.documentType)
      ? translateDocumentSummary(match.meta?.summary) || dt("reconcile.taxDocument")
      : ["CREDIT"].includes(match.documentType)
        ? dt("reconcile.installmentOf", {
            current: match.meta?.paymentNumber || "",
            total: match.meta?.totalPayments || "",
          })
        : getObligationTypeDisplayLabel(match.meta);
    return `${matchSource} ${documentInfo}`.trim();
  }

  if (["TAX_DOCUMENT", "OBLIGATION", "CREDIT"].includes(match.documentType)) {
    if (match.documentType === "TAX_DOCUMENT") {
      return translateDocumentSummary(match.meta?.summary) || dt("reconcile.taxDocument");
    } else if (match.documentType === "CREDIT") {
      return dt("reconcile.installmentOf", {
        current: match.meta?.paymentNumber || "",
        total: match.meta?.totalPayments || "",
      });
    } else {
      return getObligationTypeDisplayLabel(match.meta);
    }
  } else if (["SPEND", "GIVEN", "RECIEVED"].includes(match.type)) {
    const typeLabel =
      match.type === "RECIEVED" || match.type === "RECEIVED"
        ? dt("reconcile.credit")
        : dt("reconcile.debit");
    const bankName = banksFormat[match.bankId] || match.bankId || "";
    return bankName ? dt("reconcile.throughBank", { type: typeLabel, bank: bankName }) : typeLabel;
  }
  return '';
};

/**
 * Get document ID/number
 */
export const getDocumentNumber = (match) => {
  return [DocumentType.TAX_DOCUMENT, DocumentType.OBLIGATION, DocumentType.CREDIT].includes(match.documentType)
    ? (match.meta?.documentId || match.meta?.number)
    : null;
};

/**
 * Combine upperLine with document number
 * For CREDIT documents, don't add the document number (identifier is shown separately)
 */
export const getDocumentLabelWithNumber = (match) => {
  const upperLine = getUpperLine(match);
  
  // For CREDIT documents, don't add document number - identifier is shown in folioDetalle
  if (match.documentType === DocumentType.CREDIT) {
    return upperLine;
  }
  
  const documentNumber = getDocumentNumber(match);
  return documentNumber 
    ? `${upperLine} #${documentNumber}`
    : upperLine;
};

/**
 * Extract folioDetalle
 * For CREDIT documents, return empty string (identifier is shown in description position)
 */
export const getFolioDetalle = (match) => {
  // For CREDIT documents, identifier is shown in the description position (getName), not here
  if (match.documentType === DocumentType.CREDIT) {
    return '';
  }
  
  // For transactions, bank name is now included in the label via getUpperLine
  if (["SPEND", "GIVEN", "RECIEVED"].includes(match.type)) {
    return '';
  }
  
  return ["TAX_DOCUMENT", "OBLIGATION"].includes(match.documentType) ? "" : "";
};

const normalizeMatchSource = (
  rawMatchSource,
  origin,
  innerComments = [],
  editDataInnerComments = [],
  userRelated = null,
) => {
  const normalizeLegacyValue = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'automatic_ia') return 'automatic_ai';
    if (normalized === 'automatic_bank_commission') return 'auto_comision';
    return normalized;
  };

  const inferFromUserRelated = () => {
    const userId = String(userRelated?.userId || '').toUpperCase();
    if (!userId) return null;
    if (userId === 'AUTOMATIC_RULE') return 'automatic_rule';
    if (userId === 'AUTOMATIC_STRONG_CANDIDATE') return 'automatic_strong_candidate';
    if (userId === 'AUTOMATIC_EXACT_CANDIDATES') return 'automatic_exact_candidates';
    if (userId === 'AUTOMATIC_IVA_POSTERGADO') return 'automatic_iva_postergado';
    if (userId === 'AUTOMATIC_F29_CHECK') return 'automatic_f29_check';
    if (userId === 'AUTOMATIC_AI') return 'automatic_ai';
    if (userId === 'AUTOMATIC_N_1') return 'automatic_n_1';
    if (userId === 'AUTOMATIC_AI_NOMINA') return 'automatic_ai_nomina';
    if (userId === 'AUTO_COMISION' || userId === 'AUTOMATIC_BANK_COMMISSION') return 'auto_comision';
    if (userId === 'AUTOMATIC_HEURISTIC') return 'automatic_heuristic';
    if (userId === 'AUTOMATIC_IVA_POSTERGADO') return 'automatic_iva_postergado';
    if (userId === 'AUTOMATIC_OUTSOURCE') return 'automatic_outsource';
    return null;
  };

  const knownSources = [
    'manual',
    'automatic_rule',
    'automatic_heuristic',
    'automatic_ai',
    'automatic_ai_nomina',
    'automatic_iva_postergado',
    'automatic_strong_candidate',
    'automatic_exact_candidates',
    'automatic_n_1',
    'automatic_f29_check',
    'confirmed_strong_candidate',
    'confirmed_ai_recommendation',
    'automatic_credit',
    'auto_comision',
    'automatic_outsource',
  ];

  const normalizedRaw = normalizeLegacyValue(rawMatchSource);
  const normalizedOrigin = normalizeLegacyValue(origin);

  // PRIORITY 1: If matchSource is explicitly set to a known source, return it directly
  // This ensures backend values like 'automatic_strong_candidate' are preserved
  // IMPORTANT: Check rawMatchSource FIRST before any processing to preserve backend values
  // This has the highest priority - backend explicit values should never be overridden
  if (rawMatchSource != null && rawMatchSource !== '') {
    const normalized = normalizedRaw;
    if (knownSources.includes(normalized)) {
      return normalized;
    }
  }
  
  // PRIORITY 2: If origin is explicitly set to a known source, return it directly
  // But only if matchSource wasn't already a known source
  if (origin != null && origin !== '') {
    const normalized = normalizedOrigin;
    if (knownSources.includes(normalized)) {
      return normalized;
    }
  }

  // PRIORITY 3: Infer from userRelated only if no explicit matchSource/origin was found
  // This is a fallback for legacy data or cases where backend doesn't set matchSource explicitly
  const inferredFromUser = inferFromUserRelated();
  if (inferredFromUser) {
    return inferredFromUser;
  }

  const toLower = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase();
  };

  const rawLower = toLower(normalizedRaw);
  const originLower = toLower(normalizedOrigin);

  // Combine match innerComments with editData innerComments for comprehensive checking
  const allComments = [...(Array.isArray(innerComments) ? innerComments : []), ...(Array.isArray(editDataInnerComments) ? editDataInnerComments : [])];

  // Check comments with priority: Regla > Candidato Fuerte > IA
  const hasRuleComment = allComments.some((comment) => {
    if (!comment?.comment) return false;
    return /regla/i.test(comment.comment);
  });

  const hasStrongCandidateComment = allComments.some((comment) => {
    if (!comment?.comment) return false;
    return /candidato\s*fuerte|strong\s*candidate/i.test(comment.comment);
  });

  const hasAiComment = allComments.some((comment) => {
    if (!comment?.comment) return false;
    // Keep this intentionally broad: the backend sometimes only leaves a trace in comments.
    // But exclude if it's part of a strong candidate comment to avoid false positives
    const text = String(comment.comment || '');
    if (/candidato\s*fuerte|strong\s*candidate/i.test(text)) return false;
    return /\bia\b|inteligencia\s*artificial|\bai\b|gpt|modelo/i.test(text);
  });

  // Backend may send generic "automatic" as matchSource/origin; prioritize detection order
  if (rawLower === 'automatic') {
    if (hasRuleComment) return 'automatic_rule';
    if (hasStrongCandidateComment) return 'automatic_strong_candidate';
    if (hasAiComment) return 'automatic_ai';
    return 'automatic_heuristic';
  }

  // If backend sends variants like CONFIRMED_STRONG_CANDIDATE / *strong_candidate*, normalize them.
  const strongCandidateToken = /strong[_-]?candidate|candidato\s*fuerte/i;
  if (strongCandidateToken.test(rawLower || '') || strongCandidateToken.test(originLower || '')) {
    const isConfirmed = /confirm|confirmed/i.test(rawLower || '') || /confirm|confirmed/i.test(originLower || '');
    return isConfirmed ? 'confirmed_strong_candidate' : 'automatic_strong_candidate';
  }

  // PRIORITY: Check comments in order - Regla > Candidato Fuerte > IA
  if (hasRuleComment) {
    return 'automatic_rule';
  }

  if (hasStrongCandidateComment) {
    return 'confirmed_strong_candidate';
  }

  if (originLower === 'automatic') {
    if (hasRuleComment) return 'automatic_rule';
    if (hasStrongCandidateComment) return 'automatic_strong_candidate';
    if (hasAiComment) return 'automatic_ai';
    return 'automatic_heuristic';
  }

  if (hasAiComment) {
    return 'automatic_ai';
  }

  return 'manual';
};

const isRuleMatch = (match) => {
  return (
    match?.matchSource === 'automatic_rule' ||
    match?.userRelated?.userId === 'AUTOMATIC_RULE' ||
    !!match?.executionId ||
    !!match?.ruleId
  );
};

const forceRuleMatchSource = (match, current) => {
  // Respetar cualquier fuente automática explícita (no solo IA/heurística), p. ej. F29 o IVA postergado
  const explicit = match?.matchSource;
  if (
    explicit === 'automatic_heuristic' ||
    explicit === 'automatic_ai' ||
    explicit === 'automatic_ai_nomina' ||
    explicit === 'automatic_f29_check' ||
    explicit === 'automatic_iva_postergado' ||
    explicit === 'automatic_strong_candidate' ||
    explicit === 'automatic_exact_candidates' ||
    explicit === 'automatic_n_1' ||
    explicit === 'automatic_credit' ||
    explicit === 'auto_comision'
  ) {
    return explicit;
  }
  return isRuleMatch(match) ? 'automatic_rule' : current;
};

/**
 * Normalizes match creation date values from GraphQL/Mongo shapes.
 * Returns null when the value is missing or not parseable by moment.
 */
export const resolveMatchCreationDate = (rawDate) => {
  if (rawDate === null || rawDate === undefined || rawDate === "") return null;
  if (
    typeof rawDate === "object" &&
    rawDate !== null &&
    !Array.isArray(rawDate) &&
    Object.keys(rawDate).length === 0
  ) {
    return null;
  }

  let candidate = rawDate;
  if (typeof rawDate === "object" && rawDate !== null && !Array.isArray(rawDate)) {
    if (rawDate.$date) {
      candidate = rawDate.$date;
    } else if (rawDate.date) {
      candidate = rawDate.date;
    }
  }

  const parsed = moment(candidate);
  return parsed.isValid() ? candidate : null;
};

const sameMatchKey = (a, b) => String(a ?? "") === String(b ?? "");

const matchElementMatchesRow = (element, match, matchId, matchDocId) => {
  if (match?.matchId != null && match.matchId !== "") {
    return sameMatchKey(element.matchId, match.matchId);
  }
  return (
    sameMatchKey(element.id, matchId) ||
    (element.settleId === match.settleId && match.settleId) ||
    sameMatchKey(element.docId, matchId) ||
    sameMatchKey(element.docId, matchDocId)
  );
};

const applyUserRelatedFromMatchElement = ({
  element,
  match,
  baseInnerComments,
  editDataInnerComments,
  currentMatchSource,
  matchedByEmail,
  matchCreationDate,
}) => {
  let nextMatchSource = currentMatchSource;
  let nextMatchedByEmail = matchedByEmail;
  let nextMatchCreationDate = matchCreationDate;

  if (element?.matchSource || element?.origin || element?.userRelated?.userId) {
    nextMatchSource = normalizeMatchSource(
      element.matchSource,
      element.origin,
      element.innerComments || baseInnerComments,
      editDataInnerComments,
      element?.userRelated,
    );
    nextMatchSource = forceRuleMatchSource(element, nextMatchSource);
  }

  if (!nextMatchedByEmail && element?.userRelated?.email) {
    nextMatchedByEmail = element.userRelated.email;
    nextMatchCreationDate =
      resolveMatchCreationDate(element.userRelated.date) ?? nextMatchCreationDate;
  } else if (!nextMatchCreationDate && element?.userRelated?.date) {
    nextMatchCreationDate = resolveMatchCreationDate(element.userRelated.date);
  }

  return {
    matchSource: nextMatchSource,
    matchedByEmail: nextMatchedByEmail,
    matchCreationDate: nextMatchCreationDate,
  };
};

const findUserRelatedInMatchArray = (
  matches,
  match,
  matchId,
  matchDocId,
  baseInnerComments,
  editDataInnerComments,
  currentMatchSource,
  matchedByEmail,
  matchCreationDate,
) => {
  if (!Array.isArray(matches) || matches.length === 0) {
    return { matchSource: currentMatchSource, matchedByEmail, matchCreationDate };
  }

  let result = { matchSource: currentMatchSource, matchedByEmail, matchCreationDate };

  matches.forEach((element) => {
    if (!matchElementMatchesRow(element, match, matchId, matchDocId)) return;
    result = applyUserRelatedFromMatchElement({
      element,
      match,
      baseInnerComments,
      editDataInnerComments,
      currentMatchSource: result.matchSource,
      matchedByEmail: result.matchedByEmail,
      matchCreationDate: result.matchCreationDate,
    });
  });

  return result;
};

/**
 * Get user email and date who made the match
 * Returns an object with matchedByEmail and matchCreationDate
 */
export const getMatchUserInfo = (match, editData) => {
  let matchedByEmail = '';
  let matchCreationDate = null;
  let matchSource = 'manual'; // Default to manual
  const baseInnerComments = match?.innerComments || [];

  const hasStrongCandidateTraceForThisMatch = () => {
    const notes = editData?.innerComments || editData?.meta?.innerComments || [];
    if (!Array.isArray(notes) || notes.length === 0) return false;

    const matchDocId = match?.docId || match?.id;
    if (!matchDocId) return false;

    return notes.some((note) => {
      const text = String(note?.comment || '');
      if (!/candidato\s*fuerte|strong\s*candidate/i.test(text)) return false;
      // Scope it to this matched document to avoid mislabeling other matches.
      return text.includes(String(matchDocId));
    });
  };

  const isUniqueStrongCandidateFromEditData = () => {
    const strongCandidates = editData?.strongMatchCandidates;
    if (!Array.isArray(strongCandidates) || strongCandidates.length !== 1) return false;
    const onlyCandidateId = strongCandidates[0]?.documentId;
    if (!onlyCandidateId) return false;
    const matchDocId = match?.docId || match?.id;
    if (!matchDocId) return false;
    return String(onlyCandidateId) === String(matchDocId);
  };
  
  // PRIORITY 0: If backend indicates this is a rule match, always treat as automatic_rule
  matchSource = forceRuleMatchSource(match, matchSource);

  // Get editData innerComments to pass to normalizeMatchSource
  const editDataInnerComments = editData?.innerComments || editData?.meta?.innerComments || [];

  // PRIORITY 1: Get matchSource from the match object directly (highest priority)
  // IMPORTANT: Always call normalizeMatchSource even if matchSource/origin don't exist
  const normalized = normalizeMatchSource(
    match?.matchSource,
    match?.origin,
    baseInnerComments,
    editDataInnerComments,
    match?.userRelated,
  );
  matchSource = normalized;

  // Get user info from match if available
  if (match?.userRelated?.email) {
    matchedByEmail = match.userRelated.email;
    matchCreationDate = resolveMatchCreationDate(match.userRelated.date);
  } else if (match?.userRelated?.date) {
    matchCreationDate = resolveMatchCreationDate(match.userRelated.date);
  }

  // Support both GraphQL `id` and Mongo-style `_id` shapes.
  const matchId = match?.id ?? match?._id;
  const matchDocId = match?.docId;

  // PRIORITY 2: If matchSource is still 'manual' and we have editData, search in match arrays
  if (matchSource === 'manual' && editData) {
    const inMatchResult = findUserRelatedInMatchArray(
      editData?.meta?.matchs?.inMatch,
      match,
      matchId,
      matchDocId,
      baseInnerComments,
      editDataInnerComments,
      matchSource,
      matchedByEmail,
      matchCreationDate,
    );
    matchSource = inMatchResult.matchSource;
    matchedByEmail = inMatchResult.matchedByEmail;
    matchCreationDate = inMatchResult.matchCreationDate;

    const outMatchResult = findUserRelatedInMatchArray(
      editData?.meta?.matchs?.outMatch,
      match,
      matchId,
      matchDocId,
      baseInnerComments,
      editDataInnerComments,
      matchSource,
      matchedByEmail,
      matchCreationDate,
    );
    matchSource = outMatchResult.matchSource;
    matchedByEmail = outMatchResult.matchedByEmail;
    matchCreationDate = outMatchResult.matchCreationDate;

    // Also check editData.matchs (for transactions)
    if (matchSource === 'manual' && editData?.matchs) {
      editData?.matchs?.forEach((element) => {
        if (
          (match?.matchId != null && match.matchId !== ""
            ? sameMatchKey(element.matchId, match.matchId)
            : (
              sameMatchKey(element.docId, matchId) ||
              sameMatchKey(element.docId, matchDocId) ||
              sameMatchKey(element.id, matchId) ||
              (element.settleId === match.settleId && match.settleId) ||
              sameMatchKey(element.id, matchDocId)
            ))
        ) {
          const applied = applyUserRelatedFromMatchElement({
            element,
            match,
            baseInnerComments,
            editDataInnerComments,
            currentMatchSource: matchSource,
            matchedByEmail,
            matchCreationDate,
          });
          matchSource = applied.matchSource;
          matchedByEmail = applied.matchedByEmail;
          matchCreationDate = applied.matchCreationDate;
        }
      });
    }
  }

  // PRIORITY 2b: matchHistory fallback when date is still missing
  if (!matchCreationDate && Array.isArray(editData?.matchHistory)) {
    const historyEntry = editData.matchHistory.find((entry) => {
      if (entry?.event !== 'MATCH') return false;
      return (
        sameMatchKey(entry.matchId, matchId) ||
        sameMatchKey(entry.matchId, matchDocId)
      );
    });
    if (historyEntry?.date) {
      matchCreationDate = resolveMatchCreationDate(historyEntry.date);
    }
    if (!matchedByEmail && historyEntry?.userEmail) {
      matchedByEmail = historyEntry.userEmail;
    }
  }

  // PRIORITY 3: Frontend inference fallback
  // If backend still returns manual but we KNOW the user matched the unique strong candidate,
  // show it as confirmed strong candidate to avoid misleading “manual” label.
  if (matchSource === 'manual' && isUniqueStrongCandidateFromEditData()) {
    matchSource = 'confirmed_strong_candidate';
  }

  // PRIORITY 4: Persisted trace comment fallback (survives refetch)
  if (matchSource === 'manual' && hasStrongCandidateTraceForThisMatch()) {
    matchSource = 'confirmed_strong_candidate';
  }
  
  const isBotAttributionEmail = (email) => {
    const lower = String(email || '').toLowerCase();
    return lower === 'bot@smartcfo.cl' || lower === 'sistema@smartcfo.cl';
  };

  // IMPORTANT: displayMatchSource should show "manual" para las fuentes que requieren acción humana
  // pero donde queremos conservar el matchSource real para métricas.
  // - confirmed_strong_candidate: Usuario confirmó candidato fuerte único (salvo bot → candidato fuerte auto)
  // - confirmed_ai_recommendation: Usuario aprobó recomendación de IA (Match Recommender o candidatos débiles)
  // El matchSource original sigue retornándose para filtros.
  const manualDisplaySources = new Set(['confirmed_strong_candidate', 'confirmed_ai_recommendation']);
  // Show IVA postergado / F29 check visually as IA (purple badge) while keeping underlying matchSource
  const displayMatchSource = (() => {
    if (
      matchSource === 'confirmed_strong_candidate' &&
      isBotAttributionEmail(matchedByEmail)
    ) {
      return 'automatic_strong_candidate';
    }
    if (manualDisplaySources.has(matchSource)) return 'manual';
    if (matchSource === 'automatic_ai_nomina') return 'automatic_ai';
    if (matchSource === 'automatic_n_1') return 'automatic_ai';
    if (matchSource === 'automatic_iva_postergado') return 'automatic_ai';
    if (matchSource === 'automatic_f29_check') return 'automatic_ai';
    if (matchSource === 'auto_comision') return 'automatic_ai';
    return matchSource;
  })();

  if (
    matchedByEmail &&
    String(matchedByEmail).toLowerCase() === 'sistema@smartcfo.cl'
  ) {
    matchedByEmail = 'bot@smartcfo.cl';
  }

  return { matchedByEmail, matchCreationDate, matchSource, displayMatchSource };
};

